import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Step,
  AnnotatedScreen,
  Quiz,
} from "@/tutorial/components/TutorialComponents";
import { cn } from "@/lib/utils";

const REQUIRED = [
  { n: 1, t: "Code pénal national" },
  { n: 2, t: "Loi anticorruption nationale" },
  { n: 3, t: "Loi organique des finances publiques" },
];

const OPTIONAL = [
  { n: 4, t: "Convention de Mérida (CNUCC)" },
  { n: 5, t: "Statut de la Cour des Comptes" },
  { n: 6, t: "Loi sur la transparence budgétaire" },
];

const SEVERITY = [
  {
    level: 1,
    color: "yellow",
    title: "Niveau 1 — < 30 jours de vie prélevés illégalement",
    subtitle: "Équivalent à moins d'un mois de vie humaine.",
    sanctions: "Amendes, remboursement, sanctions disciplinaires.",
    border: "border-yellow-500",
    bg: "bg-yellow-500/10",
    text: "text-yellow-700 dark:text-yellow-300",
    badge: "🟡",
  },
  {
    level: 2,
    color: "orange",
    title: "Niveau 2 — Entre 30 jours et 1 an de vie",
    subtitle: "Équivalent à 1 à 12 mois de vie humaine prélevés.",
    sanctions: "Privation de liberté (emprisonnement).",
    border: "border-orange-500",
    bg: "bg-orange-500/10",
    text: "text-orange-700 dark:text-orange-300",
    badge: "🟠",
  },
  {
    level: 3,
    color: "red",
    title: "Niveau 3 — Plus d'un an de vie",
    subtitle: "Des années d'existence humaine volées à la collectivité.",
    sanctions:
      "Assimilé à une atteinte à la personne. Lourdes peines + confiscation totale.",
    border: "border-red-500",
    bg: "bg-red-500/10",
    text: "text-red-700 dark:text-red-300",
    badge: "🔴",
  },
];

const RESPONSIBILITIES = {
  professional: {
    label: "Professionnelle",
    desc: "Manquements aux obligations déontologiques de l'agent public ou de l'élu, traités par les juridictions ordinales et par les chambres de discipline.",
    examples: [
      "Avertissement, blâme, mutation d'office",
      "Suspension temporaire d'exercice",
      "Radiation des cadres",
      "Inscription au registre des sanctions de l'autorité de tutelle",
    ],
  },
  civil: {
    label: "Civile",
    desc: "Réparation patrimoniale du préjudice causé à la collectivité ou à un tiers : restitution intégrale du montant détourné, dommages-intérêts.",
    examples: [
      "Restitution du principal détourné",
      "Intérêts au taux légal depuis la date des faits",
      "Dommages-intérêts au profit de l'État",
      "Saisie conservatoire des biens",
    ],
  },
  administrative: {
    label: "Administrative",
    desc: "Sanctions prises par l'administration ou par la juridiction administrative (Cour des comptes, conseil de discipline budgétaire).",
    examples: [
      "Mise en débet par la Cour des comptes",
      "Inéligibilité aux charges publiques",
      "Interdiction de soumissionner aux marchés publics",
      "Confiscation des indemnités perçues",
    ],
  },
  criminal: {
    label: "Pénale",
    desc: "Application du code pénal et des lois spéciales : détournement, concussion, prise illégale d'intérêts, blanchiment, enrichissement illicite.",
    examples: [
      "Emprisonnement (gravité proportionnelle au niveau vitaliste)",
      "Amendes proportionnelles au préjudice",
      "Confiscation des biens issus de l'infraction",
      "Privation de droits civiques et de famille",
    ],
  },
};

export default function Chapter6() {
  const [tab, setTab] = useState("professional");
  return (
    <ChapterShell id={6}>
      <Section
        id="section-6-1"
        eyebrow="6.1"
        title="Uploader les textes légaux"
      >
        <p>
          L'analyse juridique exploite votre bibliothèque de textes pour
          qualifier les infractions repérées dans le budget. Plus la
          bibliothèque est complète, plus l'analyse est fine.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4">
            <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
              Textes obligatoires
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {REQUIRED.map((r) => (
                <li key={r.n} className="flex items-start gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">
                    {r.n}
                  </span>
                  <span>{r.t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-muted/40 p-4">
            <div className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
              Textes optionnels
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              {OPTIONAL.map((r) => (
                <li key={r.n} className="flex items-start gap-2">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted-foreground text-[10px] font-bold text-background">
                    {r.n}
                  </span>
                  <span>{r.t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section
        id="section-6-2"
        eyebrow="6.2"
        title="Lire le tableau des infractions"
      >
        <AnnotatedScreen
          src="/tutorial/images/chapter6-infractions.png"
          alt="Tableau des infractions détectées"
          annotations={[
            { x: 12, y: 50, label: "①", text: "Infraction : qualification juridique." },
            { x: 32, y: 50, label: "②", text: "Montant (UM) : préjudice financier brut." },
            { x: 52, y: 50, label: "③", text: "Jours de vie : traduction vitaliste." },
            { x: 72, y: 50, label: "④", text: "Niveau 1 / 2 / 3 : gravité vitaliste." },
            { x: 90, y: 50, label: "⑤", text: "Article applicable : extrait du texte légal." },
          ]}
        />
        <Step number={1} title="Colonne « Infraction »">
          Qualification juridique reconnue (détournement, concussion, etc.).
        </Step>
        <Step number={2} title="Colonne « Montant (UM) »">
          Préjudice financier brut tel qu'il ressort des documents budgétaires.
        </Step>
        <Step number={3} title="Colonne « Jours de vie »">
          Conversion vitaliste : montant ÷ salaire journalier de référence.
        </Step>
        <Step number={4} title="Colonne « Niveau »">
          Échelle de gravité 1/2/3 — voir section suivante.
        </Step>
        <Step number={5} title="Colonne « Article applicable »">
          Référence textuelle issue de la bibliothèque uploadée.
        </Step>
      </Section>

      <Section
        id="section-6-3"
        eyebrow="6.3"
        title="Les 3 niveaux de gravité vitaliste"
      >
        <div className="space-y-3">
          {SEVERITY.map((s) => (
            <div
              key={s.level}
              data-testid={`chapter-6-severity-${s.level}`}
              className={cn(
                "rounded-xl border-l-4 p-5 shadow-sm",
                s.border,
                s.bg
              )}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl" aria-hidden="true">
                  {s.badge}
                </span>
                <div className={cn("text-sm font-bold uppercase tracking-wider", s.text)}>
                  {s.title}
                </div>
              </div>
              <p className="mt-2 text-sm italic text-foreground/85">
                {s.subtitle}
              </p>
              <p className="mt-1 text-sm">
                <span className="font-semibold">Sanctions :</span> {s.sanctions}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section
        id="section-6-4"
        eyebrow="6.4"
        title="Les 4 types de responsabilités"
      >
        <Tabs value={tab} onValueChange={setTab} data-testid="chapter-6-tabs">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
            {Object.entries(RESPONSIBILITIES).map(([k, v]) => (
              <TabsTrigger
                key={k}
                value={k}
                data-testid={`chapter-6-tab-${k}`}
              >
                {v.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {Object.entries(RESPONSIBILITIES).map(([k, v]) => (
            <TabsContent key={k} value={k} className="mt-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="text-sm leading-relaxed text-foreground/90">
                  {v.desc}
                </div>
                <div className="mt-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  Exemples de sanctions
                </div>
                <ul className="mt-2 space-y-1 text-sm">
                  {v.examples.map((e, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold" />
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </Section>

      <Section id="section-6-5" eyebrow="Validation" title="Quiz du chapitre">
        <Quiz
          question="Un détournement de 50 000 UM avec un salaire journalier de 16,67 UM représente combien de jours de vie ?"
          options={["300 jours", "3 000 jours", "30 000 jours"]}
          correct={1}
          explanation="50 000 ÷ 16,67 ≈ 3 000 jours, soit environ 8,2 années d'existence humaine confisquées."
        />
      </Section>
    </ChapterShell>
  );
}
